import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { Bot, CheckCircle, Headphones, RefreshCw, Send, X } from 'lucide-react';

// AI Support Chat Component for Employer Dashboard
export default function EmployerAiSupportChat({ onClose }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `👋 Hi! I'm your AI support assistant for the Employer Dashboard. I can help you with:\n\n• Posting and managing jobs\n• Worker management\n• Payments and billing\n• Group Homes features\n• Analytics and reporting\n\nHow can I help you today?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [exchangeCount, setExchangeCount] = useState(0);
  const [showEscalation, setShowEscalation] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [ticketCreated, setTicketCreated] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await apiCall('/api/support/ai-chat', {
        method: 'POST',
        body: JSON.stringify({
          message: input,
          context: 'employer_dashboard',
          history: messages.slice(-10),
          exchange_count: exchangeCount
        })
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.response
      }]);
      
      setExchangeCount(response.exchange_count || exchangeCount + 1);
      
      // Show escalation option if suggested by backend (after 3+ exchanges)
      if (response.suggest_escalation) {
        setShowEscalation(true);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, I encountered an issue. Please try again or contact our support team.'
      }]);
      setShowEscalation(true);
    } finally {
      setLoading(false);
    }
  };

  const createSupportTicket = async () => {
    setCreatingTicket(true);
    try {
      const response = await apiCall('/api/support/tickets', {
        method: 'POST',
        body: JSON.stringify({
          subject: 'Support Request - Escalated from AI Chat',
          description: `User requested live agent assistance after ${exchangeCount} AI exchanges.`,
          context: 'employer_dashboard',
          conversation_history: messages,
          priority: 'high'
        })
      });
      
      setTicketCreated(true);
      setMessages(prev => [...prev, {
        role: 'system',
        content: `✅ **Support ticket created!**\n\nTicket ID: ${response.ticket_id}\n\nA live support agent will contact you shortly. You'll receive a notification when they respond.\n\nIn the meantime, feel free to continue asking me questions!`
      }]);
      toast.success('Support ticket created! An agent will contact you soon.');
    } catch (error) {
      toast.error('Failed to create support ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const quickQuestions = [
    'How do I post a new job?',
    'How do I manage workers?',
    'How do I view my payments?',
    'What is Group Homes?',
    'How do I check analytics?'
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg h-[650px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold">AI Support Assistant</h3>
                <p className="text-sm text-white/80">Your platform helper</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Quick Questions */}
        {messages.length <= 1 && (
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
            <div className="flex flex-wrap gap-2">
              {quickQuestions.map((q) => (
                <button
                  key={q}
                  onClick={() => {
                    setInput(q);
                    setTimeout(() => {
                      const btn = document.querySelector('[data-testid="employer-ai-send"]');
                      btn?.click();
                    }, 100);
                  }}
                  className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-xs rounded-full hover:bg-indigo-50 hover:border-indigo-300 transition"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
          {messages.map((msg, idx) => (
            <div
              key={msg.id || `${msg.role}-${idx}`}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[85%] ${msg.role === 'user' ? '' : 'flex items-start gap-2'}`}>
                {msg.role === 'assistant' && (
                  <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex-shrink-0 mt-1">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className={`px-4 py-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white rounded-br-sm'
                    : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm shadow-sm'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-start gap-2">
              <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="px-4 py-3 bg-white border border-slate-200 rounded-2xl rounded-bl-sm">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-indigo-600 animate-spin" />
                  <span className="text-sm text-slate-500">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-200 bg-white">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Ask me anything about the platform..."
              className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
              data-testid="employer-ai-input"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 transition"
              data-testid="employer-ai-send"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          
          {/* Live Agent Escalation */}
          {showEscalation && !ticketCreated && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Headphones className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">Need more help?</p>
                  <p className="text-xs text-amber-700 mt-0.5">Connect with a live support agent who can assist you directly.</p>
                </div>
                <button
                  onClick={createSupportTicket}
                  disabled={creatingTicket}
                  className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition flex items-center gap-2"
                  data-testid="employer-escalate-to-agent-btn"
                >
                  {creatingTicket ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Headphones className="w-4 h-4" />
                  )}
                  Talk to Agent
                </button>
              </div>
            </div>
          )}
          
          {ticketCreated && (
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Support ticket created! An agent will contact you soon.</span>
              </div>
            </div>
          )}
          
          {!showEscalation && !ticketCreated && (
            <p className="text-xs text-slate-400 mt-2 text-center">
              Need human help? <button onClick={() => setShowEscalation(true)} className="text-indigo-600 hover:underline">Contact Live Support</button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
